import React from "react";
import ReactDOM from "react-dom/client";
import "@/index.css";
import App from "@/App";

// ---------------------------------------------------------------------------
// Production self-heal: rewrite outgoing API calls to the current origin.
//
// CRA bakes process.env.REACT_APP_BACKEND_URL into the JS bundle at BUILD
// time. If the build was kicked off with the wrong env var (e.g. an old
// preview pod URL) every fetch points at a dead backend and the deployed
// site looks broken even though the production backend is alive at the
// same origin we're served from.
//
// Rather than wait for a clean redeploy, we patch `window.fetch` to
// rewrite ANY request that targets a known-stale Emergent preview URL
// back to whatever origin the page is currently being served from.
// Safe-by-construction: only fires when the page is served from a
// production host AND the URL being called is one of the known stale
// preview/host patterns.
// ---------------------------------------------------------------------------
(() => {
  if (typeof window === 'undefined' || typeof window.fetch !== 'function') return;
  const STALE_HOST_PATTERNS = [
    /\.preview\.emergentagent\.com$/i,
    /\.emergent\.host$/i,
  ];
  const origFetch = window.fetch.bind(window);
  const pageOrigin = window.location.origin;
  // Only self-heal when we're served from a real custom-domain prod host —
  // we never want to touch outgoing requests in local dev or preview pods.
  const isPlatformDefaultHost = STALE_HOST_PATTERNS.some(p => p.test(window.location.host));
  if (isPlatformDefaultHost) return;

  window.fetch = (input, init) => {
    try {
      let url = typeof input === 'string' ? input : (input && input.url) || '';
      if (url && /^https?:\/\//i.test(url)) {
        const u = new URL(url);
        if (STALE_HOST_PATTERNS.some(p => p.test(u.host))) {
          const rewritten = pageOrigin + u.pathname + u.search + u.hash;
          // eslint-disable-next-line no-console
          console.info('[self-heal] rewriting stale API call', u.host, '→', pageOrigin);
          if (typeof input === 'string') {
            return origFetch(rewritten, init);
          }
          // Request object — rebuild with new URL preserving init.
          return origFetch(new Request(rewritten, input), init);
        }
      }
    } catch (_) { /* fall through to original */ }
    return origFetch(input, init);
  };

  // Also patch XMLHttpRequest so axios (used by Claim flows and AuthContext)
  // self-heals the same way as fetch.
  if (typeof window.XMLHttpRequest === 'function') {
    const origOpen = window.XMLHttpRequest.prototype.open;
    window.XMLHttpRequest.prototype.open = function (method, url, ...rest) {
      try {
        if (typeof url === 'string' && /^https?:\/\//i.test(url)) {
          const u = new URL(url);
          if (STALE_HOST_PATTERNS.some(p => p.test(u.host))) {
            const rewritten = pageOrigin + u.pathname + u.search + u.hash;
            // eslint-disable-next-line no-console
            console.info('[self-heal] rewriting stale XHR call', u.host, '→', pageOrigin);
            return origOpen.call(this, method, rewritten, ...rest);
          }
        }
      } catch (_) { /* fall through */ }
      return origOpen.call(this, method, url, ...rest);
    };
  }
})();

// Disable react-error-overlay for cross-origin script errors
// This error overlay is only shown in development mode
if (process.env.NODE_ENV === 'development') {
  const originalConsoleError = console.error;
  console.error = (...args) => {
    // Suppress Script error messages from cross-origin scripts
    if (args[0] && typeof args[0] === 'string' && args[0].includes('Script error')) {
      return;
    }
    originalConsoleError.apply(console, args);
  };
}

// Suppress cross-origin script errors (like GHL widget)
window.addEventListener('error', (event) => {
  if (event.message === 'Script error.' || 
      (event.filename && event.filename.includes('leadconnector'))) {
    event.preventDefault();
    event.stopPropagation();
    return true;
  }
}, true);

// Also handle unhandled promise rejections from external scripts
window.addEventListener('unhandledrejection', (event) => {
  if (event.reason && event.reason.toString().includes('leadconnector')) {
    event.preventDefault();
    return true;
  }
});

const root = ReactDOM.createRoot(document.getElementById("root"));
root.render(
  <React.StrictMode>
    <App />
  </React.StrictMode>
);
